import { Block } from '@components/core-ui/Block';
import { Textarea } from '@components/core-ui/form-elements/Textarea';
import { RepositoryFilesBlock } from '@pages/User/RepositoryPage/components/RepositoryFilesBlock';
import { useAuthContext } from '@providers/AuthProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { routes } from '@routes';
import { PrismaRepositoryAttachmentDto, PrismaUserDto } from 'app/API';
import { EACCEPTED_AUDIO_FORMATS } from 'app/types/enums/EACCEPTED_AUDIO_FORMATS';
import { EACCEPTED_DOCUMENT_FORMATS } from 'app/types/enums/EACCEPTED_DOCUMENT_FORMATS';
import { EPermission } from 'app/types/enums/EPermission';
import { AssigneeBlockContent } from './styles/AssigneeBlockContent';
import { Input } from '@components/core-ui/form-elements/Input';
import { StyledLink } from './styles/StyledLink';

export type TForm = {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  notes: string;
  assignee: PrismaUserDto | null;
  audioFiles: PrismaRepositoryAttachmentDto[];
  documentFiles: PrismaRepositoryAttachmentDto[];
};

type TProps = {
  repositoryId: string;
  organizationSlug: string;
  documentAttachments: PrismaRepositoryAttachmentDto[];
  audioAttachments: PrismaRepositoryAttachmentDto[];
  isFilesLoading: boolean;
  form: TForm;
  setForm: (form: TForm) => void;
};

export const MainBlock = ({
  repositoryId,
  organizationSlug,
  documentAttachments,
  audioAttachments,
  isFilesLoading,
  form,
  setForm,
}: TProps) => {
  const { organization } = useAuthContext();

  const notesDescription = <p>You can provide additional information</p>;
  const attachmentsDescription = <p>You can provide additional documents, exhibits and audio files.</p>;
  const audioAttachmentsDescription = <p>You can add video and audio files to submit as jobs.</p>;
  const assigneeDescription = <p>You can assign a case to a specific user</p>;

  return (
    <>
      <Block title="Provide a unique title for the case">
        <Input onChange={() => {}} name="name" label="Title" value={form.name ?? ''} required={true} disabled={true} />
      </Block>
      <Block title="Case link">
        <Input
          onChange={() => {}}
          name="name"
          label="Case ID"
          value={form.slug ?? ''}
          required={true}
          disabled={true}
        />
      </Block>
      <Block title="Notes" description={notesDescription}>
        <Textarea
          onChange={() => {}}
          name="notes"
          label="Type your notes here"
          value={form.notes ?? ''}
          disabled={true}
        />
      </Block>
      <Block title="Attachments" description={attachmentsDescription}>
        {!isFilesLoading ? (
          <RepositoryFilesBlock
            key={`documents-${repositoryId}`}
            repositoryId={repositoryId}
            organizationSlug={organizationSlug}
            accept={EACCEPTED_DOCUMENT_FORMATS.join(', ')}
            attachments={documentAttachments}
            onUploadingChange={() => {}}
            onAttachmentRemoved={(attachmentId) =>
              setForm({
                ...form,
                documentFiles: form.documentFiles.filter((attachment) => attachment.id !== attachmentId),
              })
            }
          />
        ) : null}
        <div className="uploadDescription">
          Upload your files here. You can upload multiple files at once. Supported file types: .doc, .docx, .txt, .pdf,
          .rtf
        </div>
      </Block>

      <Block title="Audio files" description={audioAttachmentsDescription}>
        {!isFilesLoading ? (
          <RepositoryFilesBlock
            key={`audio-${repositoryId}`}
            repositoryId={repositoryId}
            organizationSlug={organizationSlug}
            accept={EACCEPTED_AUDIO_FORMATS.join(',')}
            attachments={audioAttachments}
            allowPlayback={true}
            onUploadingChange={() => {}}
            onAttachmentRemoved={(attachmentId) =>
              setForm({
                ...form,
                audioFiles: form.audioFiles.filter((attachment) => attachment.id !== attachmentId),
              })
            }
          />
        ) : null}
        <div className="uploadDescription">
          Upload audio files here. You can upload multiple files at once. Supported file types include: Zoom
          multi-channel audio, For The Record (FTR), MP3, WAV and other audio formats.
        </div>
      </Block>

      <Restricted to={EPermission.editRepositories}>
        {repositoryId ? (
          <Block title="Assignee" description={assigneeDescription}>
            <AssigneeBlockContent>
              {form.assignee ? (
                <div className="assignee">
                  <StyledLink to={routes.userDetails.make(organization?.slug ?? '', form.assignee?.id)}>
                    <p className="assignName">
                      {form.assignee?.name} {form.assignee?.lastname}
                    </p>
                    <p className="assignEmail">{form.assignee?.email || ''}</p>
                  </StyledLink>
                </div>
              ) : null}
            </AssigneeBlockContent>
          </Block>
        ) : null}
      </Restricted>
    </>
  );
};
